import fetch from 'isomorphic-unfetch'
import api from '#/configs/api'
import { stateLookup } from './stateLookup'

type IndexItem = {
  title: string
  url: string
}

type Index = {
  state?: string
  county?: string
  city?: string
  street?: string

  items: Array<IndexItem>
}

function nameLookup(index: Index): { kind: string, name: string } {
  const { state: stateCode, county, city, street } = index

  const stateName = stateLookup(String(stateCode)) ?? stateCode

  if (street) return { kind: 'street', name: `${street}, ${city}, ${county} County, ${stateCode}` }
  if (city) return { kind: 'city', name: `${city}, ${county} County, ${stateCode}` }
  if (county) return { kind: 'county', name: `${county} County, ${stateCode}` }
  if (stateCode) return { kind: 'state', name: `${stateName}` }

  return { kind: 'national', name: 'USA' }
}

function title(index: Index): string {
  const { kind, name } = nameLookup(index)
  let title = 'USA'
  switch (kind) {
    case 'street':
      title = `Addresses on ${name}`
      break
    case 'city':
      title = `Streets in ${name}`
      break
    case 'county':
      title = `Cities in ${name}`
      break
    case 'state':
      title = `Counties in ${name}`
      break
    default:
      break
  }
  return title
}

export type SitemapProcessed = {
  url: string,
  title: string,
  items: {
    title: string,
    url: string,
    metrics: number[]
  }[],
  metrics?: number[],
  name?: string
  pageCount?: number
  error?: boolean
}

type GetLinksProps = {
  url: string,
  page?: number
}

export async function getLinks({
  url,
  page
}:GetLinksProps): Promise<SitemapProcessed> {
  try {
    const indexUrl = api.sitemapRoot + url.replace('/sitemap/usa', '') + '/index.json'
    let pageToUse = page ? page - 1 : undefined

    const indexRes = await fetch(indexUrl)
    if (indexRes.status !== 200) {
      return {
        url,
        items: [],
        title: indexRes.status.toString(),
        error: true
      }
    }

    const indexData = await indexRes.json()
    if (pageToUse && indexData.pageCount < pageToUse) {
      return {
        url,
        items: [],
        title: indexRes.status.toString(),
        error: true
      }
    }

    // top level USA does not have pages
    if (url.toLowerCase().match(/\/usa$/) !== null) {
      return {
        url,
        items: indexData.items,
        title: title(indexData),
        metrics: indexData.metrics,
        pageCount: Number(indexData.pageCount),
        name: nameLookup(indexData).name
      }
    }

    // this code randomly selects a page of 25 addresses to use if no page is specified
    // use case is on the PDP page
    if (pageToUse === undefined) {
      pageToUse = Math.floor(Math.random() * indexData.pageCount)
    }

    const s3Url = indexUrl.replace('.json', `_${pageToUse}.json`)
    const res = await fetch(s3Url)

    if (res.status !== 200) {
      return {
        url,
        items: [],
        title: res.status.toString(),
        error: true
      }
    }

    const index = await res.json()
    return {
      url,
      items: index.items,
      title: title(index),
      metrics: index.metrics,
      pageCount: Number(index.pageCount),
      name: nameLookup(index).name
    }
  } catch (error) {
    return { url, items: [], title: '400', error: true }
  }
}
